import { Injectable } from '@angular/core';
import type { Type } from '@angular/core';
import createDebug from 'debug';

const debug = createDebug('AramisProcessCenter:ComponentService');

export interface ComponentMetadata {
  name: string;
  selector: string;
  displayName: string;
  description: string;
  inputs: readonly { name: string; type: string }[];
  outputs: readonly { name: string; type: string }[];
  isDialog: boolean;
  isCustomElement: boolean;
  allowChildren: boolean;
  metadata?: Record<string, unknown>;
}

@Injectable({ providedIn: 'root' })
export class ComponentService {
  readonly names = [
    'Accordion',
    'ActivityMappingDialog',
    'AddButton',
    'AddDialog',
    'AddField',
    'Avatar',
    'Breadcrumb',
    'BreadcrumbLevel',
    'ClaimServiceTaskButton',
    'ClaimServiceTaskDialog',
    'ClaimUserTaskButton',
    'ClaimUserTaskDialog',
    'CompleteServiceTaskButton',
    'CompleteServiceTaskDialog',
    'CompleteUserTaskButton',
    'CompleteUserTaskDialog',
    'ConfirmationDialog',
    'CreateWebhookSubscriptionDialog',
    'DeleteButton',
    'EditButton',
    'EditDialog',
    'ErrorDialog',
    'ExportTsvButton',
    'FireTimerButton',
    'FireTimerDialog',
    'HistoryDetailsDialog',
    'HistoryList',
    'ImportTsvButton',
    'ImportTsvDialog',
    'LoadFromFilesButton',
    'LoadFromFilesDialog',
    'ManageRoleAssignments',
    'MoveTokenDialog',
    'ProcessDataPropertySection',
    'ProcessDataValueSection',
    'ProcessFunctionParameterSection',
    'ProcessMessagePropertySection',
    'ProcessServicePropertySection',
    'ProcessUserInterfacePropertySection',
    'ReassignUserTaskButton',
    'ReassignUserTaskDialog',
    'RefreshButton',
    'RemoveRow',
    'ReportErrorServiceTaskButton',
    'ReportErrorServiceTaskDialog',
    'SearchBar',
    'SelectCalledProcessField',
    'SelectProcessMessageField',
    'SelectProcessRoleField',
    'SelectProcessServiceField',
    'SelectProcessUserInterfaceField',
    'SelectRoleField',
    'SelectRoleNameField',
    'SelectServiceQueueField',
    'SelectUserField',
    'SelectUserInterfaceComponent',
    'SendMessageButton',
    'SendMessageDialog',
    'SfCurrencyField',
    'SfDecimalField',
    'SfIntegerField',
    'SfPercentageField',
    'SfReferenceField',
    'SfTimezoneField',
    'StartInstanceDialog',
    'TenantChooserDialog',
    'TokenList',
    'UnclaimUserTaskButton',
    'UpdateProcessInstanceDataButton',
    'UpdateProcessInstanceDataDialog',
    'UpdateUserTaskDataButton',
    'UpdateUserTaskDataDialog',
    'WebhookEventTypesField',
  ] as const;

  readonly metadata: readonly ComponentMetadata[] = [
    {
      name: 'Accordion',
      selector: 'accordion',
      displayName: 'Accordion',
      description:
        "A data-driven expansion panel list. Takes a business object array and renders a mat-accordion with one mat-expansion-panel per item. Each panel shows the item's display name in the header and sf-fields in the body. Built-in support for adding, deleting, and navigating to an item's detail page via routerLink.",
      inputs: [
        { name: 'array', type: 'PersistedArray<any> | PersistedFormArray' },
        { name: 'defaults', type: 'Record<string, any>' },
        { name: 'hideAdd', type: 'any' },
        { name: 'includeLaunch', type: 'any' },
        { name: 'routePrefix', type: 'string' },
        { name: 'routeFunction', type: '(item: any) => string' },
        { name: 'displayNameFunction', type: '(item: any) => string' },
      ],
      outputs: [{ name: 'deleted', type: 'EventEmitter<any>' }],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'ActivityMappingDialog',
      selector: 'activity-mapping-dialog',
      displayName: 'Activity Mapping Dialog',
      description:
        'Dialog for creating or updating an activity mapping while resolving a blocked instance in the migration view. Offers move, cancel, and add-token actions with optional source version bounds. Saving updates the existing Move/Cancel rule covering the source version when there is one, otherwise creates a new rule.',
      inputs: [
        { name: 'designUuid', type: 'string' },
        { name: 'sourceVersion', type: 'number' },
        { name: 'sourceActivityId', type: 'string' },
        { name: 'sourceActivityName', type: 'string' },
        { name: 'targetActivityId', type: 'string' },
        { name: 'targetActivityName', type: 'string' },
      ],
      outputs: [{ name: 'saved', type: 'EventEmitter<void>' }],
      isDialog: true,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'AddButton',
      selector: 'add-button',
      displayName: 'Add Button',
      description:
        "A button that opens a dialog for adding a new record to a persisted array. Renders a Material raised button with an add icon. When clicked, opens an AddDialog that dynamically loads a schema-driven form for the array's entity type.",
      inputs: [
        { name: 'array', type: 'PersistedArray<any> | PersistedFormArray' },
        { name: 'label', type: 'string' },
        { name: 'defaults', type: 'Record<string, any>' },
        { name: 'dialogWidth', type: 'string' },
        { name: 'afterAddRoute', type: 'string' },
        { name: 'fields', type: 'string[]' },
        { name: 'iconButton', type: 'any' },
        { name: 'fab', type: 'any' },
      ],
      outputs: [{ name: 'added', type: 'EventEmitter<any>' }],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'AddDialog',
      selector: 'add-dialog',
      displayName: 'Add Dialog',
      description:
        "Dialog for adding a new record to a persisted array. Dynamically loads a schema-driven form using BusinessObjectService based on the array's entity name. Hidden fields are automatically disabled so they don't block form validity.",
      inputs: [
        { name: 'array', type: 'PersistedArray<any> | PersistedFormArray' },
        { name: 'label', type: 'string' },
        { name: 'defaults', type: 'Record<string, any>' },
        { name: 'fields', type: 'string[]' },
        { name: 'afterAddRoute', type: 'string' },
      ],
      outputs: [{ name: 'added', type: 'EventEmitter<any>' }],
      isDialog: true,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'AddField',
      selector: 'add-field',
      displayName: 'Add Field',
      description:
        'An inline text input for adding a new item to a business object array. Types a name, presses Enter, and the item is created with that name as its first visible property.',
      inputs: [
        { name: 'array', type: 'PersistedArray<any> | PersistedFormArray' },
        { name: 'label', type: 'string' },
        { name: 'defaults', type: 'Record<string, any>' },
        { name: 'nameCase', type: 'string' },
      ],
      outputs: [{ name: 'added', type: 'EventEmitter<any>' }],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'Avatar',
      selector: 'avatar',
      displayName: 'Avatar',
      description:
        'User avatar button with a menu showing the current user email and logout. Shows a "Switch User" option when impersonation is enabled. Additional custom links can be supplied via the `menuItems` input.',
      inputs: [{ name: 'menuItems', type: 'AvatarMenuItem[]' }],
      outputs: [
        { name: 'onMenuItemClick', type: 'EventEmitter<AvatarMenuItem>' },
      ],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'Breadcrumb',
      selector: 'breadcrumb',
      displayName: 'Breadcrumb',
      description:
        "Navigation breadcrumb container that displays child breadcrumb levels separated by delimiters.  Presentation is set with CSS custom properties rather than by overriding the component's selectors — the styles here are encapsulated, so an app-level selector can't out-specify them without `!important`. Set any of these on the `breadcrumb` element (or any ancestor) from the app's global stylesheet:  | Custom property | Default | Applies to | | --- | --- | --- | | `--apex-breadcrumb-font-size` | `14px` | every level | | `--apex-breadcrumb-font-weight` | `inherit` | every level | | `--apex-breadcrumb-line-height` | `inherit` | every level | | `--apex-breadcrumb-color` | `rgba(0, 0, 0, 0.54)` | the current (unlinked) level | | `--apex-breadcrumb-link-color` | `var(--apex-primary, #1976d2)` | linked levels | | `--apex-breadcrumb-link-hover-text-decoration` | `underline` | linked levels, on hover | | `--apex-breadcrumb-subtitle-font-size` | `80%` | level subtitles | | `--apex-breadcrumb-subtitle-font-weight` | `inherit` | level subtitles | | `--apex-breadcrumb-subtitle-color` | `rgba(0, 0, 0, 0.54)` | level subtitles | | `--apex-breadcrumb-subtitle-margin-left` | `4px` | level subtitles | | `--apex-breadcrumb-separator-content` | `'›'` | the delimiter glyph | | `--apex-breadcrumb-separator-font-family` | `inherit` | the delimiter glyph | | `--apex-breadcrumb-separator-font-size` | `inherit` | the delimiter glyph | | `--apex-breadcrumb-separator-margin-left` | `8px` | the delimiter glyph | | `--apex-breadcrumb-separator-margin-right` | `8px` | the delimiter glyph | | `--apex-breadcrumb-separator-color` | `rgba(0, 0, 0, 0.54)` | the delimiter glyph | | `--apex-breadcrumb-icon-size` | `18px` | icon levels |  To render the breadcrumb as the page heading (the AD2 presentation):  ```css breadcrumb {   --apex-breadcrumb-font-size: 24px;   --apex-breadcrumb-font-weight: 400;   --apex-breadcrumb-line-height: 40px;   --apex-breadcrumb-color: rgba(0, 0, 0, 0.87);   --apex-breadcrumb-link-color: rgba(0, 0, 0, 0.87);   --apex-breadcrumb-link-hover-text-decoration: none;   --apex-breadcrumb-subtitle-font-size: 0.8em;   --apex-breadcrumb-subtitle-font-weight: 100;   --apex-breadcrumb-subtitle-color: #999999;   --apex-breadcrumb-subtitle-margin-left: 12px;   --apex-breadcrumb-separator-content: 'keyboard_arrow_right';   --apex-breadcrumb-separator-font-family: 'Material Icons';   --apex-breadcrumb-separator-font-size: 24px;   --apex-breadcrumb-separator-margin-left: 8px;   --apex-breadcrumb-separator-margin-right: 6px; } ```  Give every hook a concrete value — never a CSS-wide keyword. `--x: inherit` makes the *custom property* inherit its own value from the parent rather than making the styled property inherit, so `var(--x, fallback)` falls through to the fallback and the override silently does nothing. To make links take the surrounding text color, name the color, as above.",
      inputs: [{ name: 'heading', type: 'boolean' }],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: true,
    },
    {
      name: 'BreadcrumbLevel',
      selector: 'breadcrumb-level',
      displayName: 'Breadcrumb Level',
      description:
        'A single level inside a Breadcrumb. Renders as a link when path is set, plain text otherwise.',
      inputs: [
        { name: 'label', type: 'string' },
        { name: 'subtitle', type: 'string' },
        { name: 'path', type: 'string' },
        { name: 'queryParams', type: 'any' },
        { name: 'icon', type: 'string' },
      ],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'ClaimServiceTaskButton',
      selector: 'claim-service-task-button',
      displayName: 'Claim Service Task Button',
      description: 'Button that opens the claim service task dialog.',
      inputs: [{ name: 'serviceTaskFormGroup', type: 'ServiceTaskFormGroup' }],
      outputs: [{ name: 'claimed', type: 'EventEmitter<void>' }],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'ClaimServiceTaskDialog',
      selector: 'claim-service-task-dialog',
      displayName: 'Claim Service Task Dialog',
      description: 'Dialog for claiming a service task with a worker ID.',
      inputs: [{ name: 'serviceTaskFormGroup', type: 'ServiceTaskFormGroup' }],
      outputs: [{ name: 'claimed', type: 'EventEmitter<void>' }],
      isDialog: true,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'ClaimUserTaskButton',
      selector: 'claim-user-task-button',
      displayName: 'Claim User Task Button',
      description: 'Button that opens the claim user task dialog.',
      inputs: [{ name: 'userTaskFormGroup', type: 'UserTaskFormGroup' }],
      outputs: [{ name: 'claimed', type: 'EventEmitter<void>' }],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'ClaimUserTaskDialog',
      selector: 'claim-user-task-dialog',
      displayName: 'Claim User Task Dialog',
      description:
        'Dialog for claiming a user task. Admins can select a user to claim on behalf of.',
      inputs: [{ name: 'userTaskFormGroup', type: 'UserTaskFormGroup' }],
      outputs: [{ name: 'claimed', type: 'EventEmitter<void>' }],
      isDialog: true,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'CompleteServiceTaskButton',
      selector: 'complete-service-task-button',
      displayName: 'Complete Service Task Button',
      description: 'Button that opens the complete service task dialog.',
      inputs: [{ name: 'serviceTaskFormGroup', type: 'ServiceTaskFormGroup' }],
      outputs: [{ name: 'completed', type: 'EventEmitter<void>' }],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'CompleteServiceTaskDialog',
      selector: 'complete-service-task-dialog',
      displayName: 'Complete Service Task Dialog',
      description:
        'Dialog for completing a service task with optional output data.',
      inputs: [{ name: 'serviceTaskFormGroup', type: 'ServiceTaskFormGroup' }],
      outputs: [{ name: 'completed', type: 'EventEmitter<void>' }],
      isDialog: true,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'CompleteUserTaskButton',
      selector: 'complete-user-task-button',
      displayName: 'Complete User Task Button',
      description: 'Button that opens the complete user task dialog.',
      inputs: [{ name: 'userTaskFormGroup', type: 'UserTaskFormGroup' }],
      outputs: [{ name: 'completed', type: 'EventEmitter<void>' }],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'CompleteUserTaskDialog',
      selector: 'complete-user-task-dialog',
      displayName: 'Complete User Task Dialog',
      description:
        'Dialog for completing a user task with optional data updates.',
      inputs: [{ name: 'userTaskFormGroup', type: 'UserTaskFormGroup' }],
      outputs: [{ name: 'completed', type: 'EventEmitter<void>' }],
      isDialog: true,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'ConfirmationDialog',
      selector: 'confirmation-dialog',
      displayName: 'Confirmation Dialog',
      description:
        'A simple Material dialog that displays a message with Cancel and Confirm buttons. Used by the confirm directive to prompt the user before destructive actions.',
      inputs: [{ name: 'message', type: 'string' }],
      outputs: [
        { name: 'confirmed', type: 'EventEmitter<void>' },
        { name: 'canceled', type: 'EventEmitter<void>' },
      ],
      isDialog: true,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'CreateWebhookSubscriptionDialog',
      selector: 'create-webhook-subscription-dialog',
      displayName: 'Create Webhook Subscription Dialog',
      description:
        'Collects the name, receiver URL, and event types for a new subscription, creates it, and then shows the generated signing secret exactly once. "Done" opens the new subscription\'s page.',
      inputs: [],
      outputs: [{ name: 'created', type: 'EventEmitter<any>' }],
      isDialog: true,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'DeleteButton',
      selector: 'delete-button',
      displayName: 'Delete Button',
      description:
        'An icon button that deletes a business object with a confirmation prompt. After deletion, navigates to the specified route.',
      inputs: [
        { name: 'object', type: 'PersistedFormGroup' },
        { name: 'afterDeleteRoute', type: 'string' },
      ],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'EditButton',
      selector: 'edit-button',
      displayName: 'Edit Button',
      description:
        'A button that opens the edit dialog for a business object. Bundles the button, the dialog and the wiring between them into one element, so a detail page gets its edit affordance without a class property, a referenceable handle, or its own choice of icon and tooltip.',
      inputs: [
        { name: 'object', type: 'PersistedFormGroup' },
        { name: 'label', type: 'string' },
        { name: 'allowDelete', type: 'any' },
        { name: 'fields', type: 'string[]' },
        { name: 'showDisabled', type: 'any' },
        { name: 'dialogWidth', type: 'string' },
        { name: 'raisedButton', type: 'any' },
      ],
      outputs: [
        { name: 'deleted', type: 'EventEmitter<void>' },
        { name: 'closed', type: 'EventEmitter<void>' },
      ],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'EditDialog',
      selector: 'edit-dialog',
      displayName: 'Edit Dialog',
      description:
        'A Material dialog for editing a business object inline. Opens as a modal, auto-renders all fields using sf-fields, and optionally provides a delete button. Used within list/table views to edit items without navigating away.',
      inputs: [
        { name: 'object', type: 'PersistedFormGroup' },
        { name: 'label', type: 'string' },
        { name: 'allowDelete', type: 'any' },
        { name: 'fields', type: 'string[]' },
        { name: 'showDisabled', type: 'any' },
      ],
      outputs: [
        { name: 'deleted', type: 'EventEmitter<void>' },
        { name: 'closed', type: 'EventEmitter<void>' },
      ],
      isDialog: true,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'ErrorDialog',
      selector: 'error-dialog',
      displayName: 'Error Dialog',
      description:
        'A Material dialog that displays a list of error messages. Provides a Close button that clears the messages and closes the dialog.',
      inputs: [{ name: 'messages', type: 'string[]' }],
      outputs: [],
      isDialog: true,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'ExportTsvButton',
      selector: 'export-tsv-button',
      displayName: 'Export Tsv Button',
      description:
        'An icon button that exports a business object array to a tab-separated values (TSV) file. Downloads the file directly in the browser.',
      inputs: [
        { name: 'array', type: 'PersistedArray<any> | PersistedFormArray' },
        { name: 'excludePropertyNames', type: 'string[] | string' },
      ],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'FireTimerButton',
      selector: 'fire-timer-button',
      displayName: 'Fire Timer Button',
      description:
        'Button that opens the fire timer dialog to manually fire a timer.',
      inputs: [{ name: 'timerFormGroup', type: 'TimerFormGroup' }],
      outputs: [{ name: 'fired', type: 'EventEmitter<void>' }],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'FireTimerDialog',
      selector: 'fire-timer-dialog',
      displayName: 'Fire Timer Dialog',
      description: 'Confirmation dialog for manually firing a timer.',
      inputs: [{ name: 'timerFormGroup', type: 'TimerFormGroup' }],
      outputs: [{ name: 'fired', type: 'EventEmitter<void>' }],
      isDialog: true,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'HistoryDetailsDialog',
      selector: 'history-details-dialog',
      displayName: 'History Details Dialog',
      description: 'Dialog that displays details for a single history entry.',
      inputs: [
        { name: 'eventType', type: 'string' },
        { name: 'timestamp', type: 'string' },
        { name: 'data', type: 'any' },
      ],
      outputs: [],
      isDialog: true,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'HistoryList',
      selector: 'history-list',
      displayName: 'History List',
      description:
        'Displays a list of history entries with relative timestamps.',
      inputs: [
        { name: 'history', type: 'any' },
        { name: 'title', type: 'string' },
      ],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'ImportTsvButton',
      selector: 'import-tsv-button',
      displayName: 'Import Tsv Button',
      description:
        'An icon button that opens a dialog for importing tab-separated values into a business object array.',
      inputs: [
        { name: 'array', type: 'PersistedArray<any> | PersistedFormArray' },
        { name: 'parentObject', type: 'PersistedFormGroup' },
        { name: 'customTemplate', type: 'string' },
        { name: 'tsvHandler', type: '(rows: string[][]) => Promise<void>' },
      ],
      outputs: [
        {
          name: 'imported',
          type: 'EventEmitter<{ added: number; updated: number }>',
        },
      ],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'ImportTsvDialog',
      selector: 'import-tsv-dialog',
      displayName: 'Import Tsv Dialog',
      description:
        "A Material dialog for pasting TSV data, with parsing, column matching, and importing records into a business object array. Offers a downloadable TSV template with the entity's importable columns.",
      inputs: [
        { name: 'array', type: 'PersistedArray<any> | PersistedFormArray' },
        { name: 'parentObject', type: 'PersistedFormGroup' },
        { name: 'customTemplate', type: 'string' },
        { name: 'tsvHandler', type: '(rows: string[][]) => Promise<void>' },
      ],
      outputs: [
        {
          name: 'imported',
          type: 'EventEmitter<{ added: number; updated: number }>',
        },
      ],
      isDialog: true,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'LoadFromFilesButton',
      selector: 'load-from-files-button',
      displayName: 'Load From Files Button',
      description:
        'Button that loads process designs from server files and shows results in a dialog.',
      inputs: [],
      outputs: [{ name: 'loaded', type: 'EventEmitter<void>' }],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'LoadFromFilesDialog',
      selector: 'load-from-files-dialog',
      displayName: 'Load From Files Dialog',
      description:
        'Dialog showing results of loading process designs from server files.',
      inputs: [{ name: 'results', type: 'any[]' }],
      outputs: [],
      isDialog: true,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'ManageRoleAssignments',
      selector: 'manage-role-assignments',
      displayName: 'Manage Role Assignments',
      description:
        'Displays a table of users and their role assignments. Rows are users (derived from role assignments), columns are roles. Checkbox icon buttons toggle role assignments. A Last Seen column shows when each user was last active, or "Never signed in" for a user added by email who has not signed in yet.',
      inputs: [{ name: 'roleNames', type: 'string' }],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'MoveTokenDialog',
      selector: 'move-token-dialog',
      displayName: 'Move Token Dialog',
      description:
        'Confirmation dialog shown before relocating a token to a different activity. Emits `confirmed` when the user chooses to move.',
      inputs: [{ name: 'activityName', type: 'string' }],
      outputs: [{ name: 'confirmed', type: 'EventEmitter<void>' }],
      isDialog: true,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'ProcessDataPropertySection',
      selector: 'process-data-property-section',
      displayName: 'Data Property',
      description: 'Custom section for editing a process data property.',
      inputs: [
        { name: 'group', type: 'ProcessDataPropertyFormGroup' },
        { name: 'hideHelpTexts', type: 'boolean' },
        { name: 'placeholders', type: 'any' },
      ],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'ProcessDataValueSection',
      selector: 'process-data-value-section',
      displayName: 'Data Value',
      description:
        "Custom section for editing a process data value. Shows the appropriate value field based on the parent data type's native type.",
      inputs: [
        { name: 'group', type: 'ProcessDataValueFormGroup' },
        { name: 'hideHelpTexts', type: 'boolean' },
        { name: 'placeholders', type: 'any' },
      ],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'ProcessFunctionParameterSection',
      selector: 'process-function-parameter-section',
      displayName: 'Function Parameter',
      description: 'Custom section for editing a process function parameter.',
      inputs: [
        { name: 'group', type: 'ProcessFunctionParameterFormGroup' },
        { name: 'hideHelpTexts', type: 'boolean' },
        { name: 'placeholders', type: 'any' },
      ],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'ProcessMessagePropertySection',
      selector: 'process-message-property-section',
      displayName: 'Message Property',
      description: 'Custom section for editing a process message property.',
      inputs: [
        { name: 'group', type: 'ProcessMessagePropertyFormGroup' },
        { name: 'hideHelpTexts', type: 'boolean' },
        { name: 'placeholders', type: 'any' },
      ],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'ProcessServicePropertySection',
      selector: 'process-service-property-section',
      displayName: 'Service Property',
      description: 'Custom section for editing a process service property.',
      inputs: [
        { name: 'group', type: 'ProcessServicePropertyFormGroup' },
        { name: 'hideHelpTexts', type: 'boolean' },
        { name: 'placeholders', type: 'any' },
      ],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'ProcessUserInterfacePropertySection',
      selector: 'process-user-interface-property-section',
      displayName: 'User Interface Property',
      description:
        'Custom section for editing a process user interface property.',
      inputs: [
        { name: 'group', type: 'ProcessUserInterfacePropertyFormGroup' },
        { name: 'hideHelpTexts', type: 'boolean' },
        { name: 'placeholders', type: 'any' },
      ],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'ReassignUserTaskButton',
      selector: 'reassign-user-task-button',
      displayName: 'Reassign User Task Button',
      description: 'Button that opens the reassign user task dialog.',
      inputs: [{ name: 'userTaskFormGroup', type: 'UserTaskFormGroup' }],
      outputs: [{ name: 'reassigned', type: 'EventEmitter<void>' }],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'ReassignUserTaskDialog',
      selector: 'reassign-user-task-dialog',
      displayName: 'Reassign User Task Dialog',
      description: 'Dialog for reassigning a user task to a different user.',
      inputs: [{ name: 'userTaskFormGroup', type: 'UserTaskFormGroup' }],
      outputs: [{ name: 'reassigned', type: 'EventEmitter<void>' }],
      isDialog: true,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'RefreshButton',
      selector: 'refresh-button',
      displayName: 'Refresh Button',
      description:
        'An icon button that re-reads a business object array or single object. Provides a one-click way to reload data using the current read configuration.',
      inputs: [
        { name: 'array', type: 'PersistedArray<any> | PersistedFormArray' },
        { name: 'object', type: 'PersistedFormGroup' },
      ],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'RemoveRow',
      selector: 'remove-row',
      displayName: 'Remove Row',
      description:
        'An icon button with confirmation that removes an item from a PersistedArray or PersistedFormArray. Pass both the `array` and the `item` so the component can call `array.remove(item)`.',
      inputs: [
        { name: 'array', type: 'any' },
        { name: 'item', type: 'any' },
        { name: 'disabled', type: 'boolean' },
      ],
      outputs: [{ name: 'removed', type: 'EventEmitter<any>' }],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'ReportErrorServiceTaskButton',
      selector: 'report-error-service-task-button',
      displayName: 'Report Error Service Task Button',
      description:
        'Button that opens the report error dialog for a service task.',
      inputs: [{ name: 'serviceTaskFormGroup', type: 'ServiceTaskFormGroup' }],
      outputs: [{ name: 'errorReported', type: 'EventEmitter<void>' }],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'ReportErrorServiceTaskDialog',
      selector: 'report-error-service-task-dialog',
      displayName: 'Report Error Service Task Dialog',
      description: 'Dialog for reporting an error on a service task.',
      inputs: [{ name: 'serviceTaskFormGroup', type: 'ServiceTaskFormGroup' }],
      outputs: [{ name: 'errorReported', type: 'EventEmitter<void>' }],
      isDialog: true,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'SearchBar',
      selector: 'search-bar',
      displayName: 'Search Bar',
      description:
        "A text input that filters a business object array by searching across the entity's properties. Applies a debounced server-side filter — `ilike` for string/text properties and exact equality for numeric properties when the search text parses as a number.  By default only non-id string-typed properties are searched. Pass `searchFields` to override that list (e.g. when the page displays an id or foreign key and users should be able to find rows by it).",
      inputs: [
        { name: 'array', type: 'PersistedArray<any> | PersistedFormArray' },
        { name: 'searchFields', type: 'string[]' },
        { name: 'baseWhere', type: 'Record<string, any>' },
      ],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'SelectCalledProcessField',
      selector: 'select-called-process-field',
      displayName: 'Select Called Process Field',
      description:
        'Schema form field that displays a dropdown of active process designs. The control value is the process design UUID.',
      inputs: [
        { name: 'control', type: 'SchemaFormControl' },
        { name: 'label', type: 'string' },
        { name: 'placeholder', type: 'string' },
        { name: 'hideHelpText', type: 'boolean' },
      ],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'SelectProcessMessageField',
      selector: 'select-process-message-field',
      displayName: 'Select Process Message Field',
      description:
        'Schema form field that displays a dropdown of the app events a process can bind as messages — what a receive task, message boundary event or message start listens for, or a send task sends. The control value is the event class name, which is the message name. The process modeler presents the Message Name field with this picker when the host registers it.',
      inputs: [
        { name: 'control', type: 'SchemaFormControl' },
        { name: 'label', type: 'string' },
        { name: 'placeholder', type: 'string' },
        { name: 'hideHelpText', type: 'boolean' },
      ],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'SelectProcessRoleField',
      selector: 'select-process-role-field',
      displayName: 'Select Process Role Field',
      description:
        'Schema form field that displays a dropdown of process roles. The control value is the role name.',
      inputs: [
        { name: 'control', type: 'SchemaFormControl' },
        { name: 'label', type: 'string' },
        { name: 'placeholder', type: 'string' },
        { name: 'hideHelpText', type: 'boolean' },
      ],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'SelectProcessServiceField',
      selector: 'select-process-service-field',
      displayName: 'Select Process Service Field',
      description:
        'Schema form field that displays a dropdown of process services. The control value is the service name.',
      inputs: [
        { name: 'control', type: 'SchemaFormControl' },
        { name: 'label', type: 'string' },
        { name: 'placeholder', type: 'string' },
        { name: 'hideHelpText', type: 'boolean' },
      ],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'SelectProcessUserInterfaceField',
      selector: 'select-process-user-interface-field',
      displayName: 'Select Process User Interface Field',
      description:
        'Schema form field that displays a dropdown of process user interface components. The control value is the component name.',
      inputs: [
        { name: 'control', type: 'SchemaFormControl' },
        { name: 'label', type: 'string' },
        { name: 'placeholder', type: 'string' },
        { name: 'hideHelpText', type: 'boolean' },
      ],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'SelectRoleField',
      selector: 'select-role-field',
      displayName: 'Select Role Field',
      description:
        'Schema form field that displays a dropdown of roles. The control value is the role id.',
      inputs: [
        { name: 'control', type: 'SchemaFormControl' },
        { name: 'label', type: 'string' },
        { name: 'placeholder', type: 'string' },
        { name: 'hideHelpText', type: 'boolean' },
      ],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'SelectRoleNameField',
      selector: 'select-role-name-field',
      displayName: 'Select Role Name Field',
      description:
        'Schema form field that displays a dropdown of roles. The control value is the role name.',
      inputs: [
        { name: 'control', type: 'SchemaFormControl' },
        { name: 'label', type: 'string' },
        { name: 'placeholder', type: 'string' },
        { name: 'hideHelpText', type: 'boolean' },
      ],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'SelectServiceQueueField',
      selector: 'select-service-queue-field',
      displayName: 'Select Service Queue Field',
      description:
        'Schema form field that displays a dropdown of services. The control value is the service name.',
      inputs: [
        { name: 'control', type: 'SchemaFormControl' },
        { name: 'label', type: 'string' },
        { name: 'placeholder', type: 'string' },
        { name: 'hideHelpText', type: 'boolean' },
      ],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'SelectUserField',
      selector: 'select-user-field',
      displayName: 'Select User Field',
      description:
        'Schema form field that displays a dropdown of users. The control value is the user id.',
      inputs: [
        { name: 'control', type: 'SchemaFormControl' },
        { name: 'label', type: 'string' },
        { name: 'placeholder', type: 'string' },
        { name: 'hideHelpText', type: 'boolean' },
      ],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'SelectUserInterfaceComponent',
      selector: 'select-user-interface-component',
      displayName: 'Select User Interface Component',
      description:
        'Schema form field that displays a dropdown of user interface components. The control value is the component name.',
      inputs: [
        { name: 'control', type: 'SchemaFormControl' },
        { name: 'label', type: 'string' },
        { name: 'placeholder', type: 'string' },
        { name: 'hideHelpText', type: 'boolean' },
      ],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'SendMessageButton',
      selector: 'send-message-button',
      displayName: 'Send Message Button',
      description:
        'Button that opens the send message dialog to trigger a message listener.',
      inputs: [
        { name: 'messageListenerFormGroup', type: 'MessageListenerFormGroup' },
      ],
      outputs: [{ name: 'sent', type: 'EventEmitter<void>' }],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'SendMessageDialog',
      selector: 'send-message-dialog',
      displayName: 'Send Message Dialog',
      description:
        'Dialog for triggering a message listener with optional data.',
      inputs: [
        { name: 'messageListenerFormGroup', type: 'MessageListenerFormGroup' },
      ],
      outputs: [{ name: 'sent', type: 'EventEmitter<void>' }],
      isDialog: true,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'SfCurrencyField',
      selector: 'sf-currency-field',
      displayName: 'Sf Currency Field',
      description:
        "A schema form field component for properties with the currency format (the Currency base type). A right-aligned input that shows the value formatted in the app's currency ($123,456.78) and edits it as a plain number. A `displayFormat` on the schema (an Excel number format code) replaces the currency display. The control holds a decimal.js Decimal; the input holds its text. Set `--sf-number-align` on a container to change the alignment.",
      inputs: [
        { name: 'control', type: 'SchemaFormControl' },
        { name: 'label', type: 'string' },
        { name: 'placeholder', type: 'string' },
        { name: 'hideHelpText', type: 'boolean' },
      ],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'SfDecimalField',
      selector: 'sf-decimal-field',
      displayName: 'Sf Decimal Field',
      description:
        "A schema form field component for properties with the decimal format (a base type over decimal.js's Decimal, other than Currency). A right-aligned input that shows the value with the locale's grouping (123,456.789) and edits it as a plain number, keeping every stored digit. A `displayFormat` on the schema (an Excel number format code) replaces that display. The control holds a decimal.js Decimal; the input holds its text. Set `--sf-number-align` on a container to change the alignment.",
      inputs: [
        { name: 'control', type: 'SchemaFormControl' },
        { name: 'label', type: 'string' },
        { name: 'placeholder', type: 'string' },
        { name: 'hideHelpText', type: 'boolean' },
      ],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'SfIntegerField',
      selector: 'sf-integer-field',
      displayName: 'Sf Integer Field',
      description:
        "A schema form field component for properties with the integer format (the Integer base type). A right-aligned input that shows the value with the locale's grouping and no decimals (123,456) and edits it as plain digits. Text with a fraction is flagged, not stored. A `displayFormat` on the schema (an Excel number format code) replaces the display. The control holds a decimal.js Decimal; the input holds its text. Set `--sf-number-align` on a container to change the alignment.",
      inputs: [
        { name: 'control', type: 'SchemaFormControl' },
        { name: 'label', type: 'string' },
        { name: 'placeholder', type: 'string' },
        { name: 'hideHelpText', type: 'boolean' },
      ],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'SfPercentageField',
      selector: 'sf-percentage-field',
      displayName: 'Sf Percentage Field',
      description:
        'A schema form field component for properties with the percentage format (the Percentage base type). The value is stored as an exact fraction (0.9812) and shown and edited as a percentage (98.12%) in a right-aligned input. A `displayFormat` on the schema (an Excel number format code) replaces the percentage display. The control holds the fraction as a decimal.js Decimal; the input holds the percentage text. Set `--sf-number-align` on a container to change the alignment.',
      inputs: [
        { name: 'control', type: 'SchemaFormControl' },
        { name: 'label', type: 'string' },
        { name: 'placeholder', type: 'string' },
        { name: 'hideHelpText', type: 'boolean' },
      ],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'SfReferenceField',
      selector: 'sf-reference-field',
      displayName: 'Sf Reference Field',
      description:
        "A schema form field component for reference foreign key properties. Displays a mat-autocomplete that searches the referenced entity by its display property and stores the selected record's id.",
      inputs: [
        { name: 'control', type: 'SchemaFormControl' },
        { name: 'label', type: 'string' },
        { name: 'placeholder', type: 'string' },
        { name: 'hideHelpText', type: 'boolean' },
      ],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'SfTimezoneField',
      selector: 'sf-timezone-field',
      displayName: 'Sf Timezone Field',
      description:
        'A schema form field component for properties with the timezone format (the Timezone base type). Displays a filter-as-you-type mat-autocomplete over a curated list of common IANA zone names. Any IANA zone name remains storable: the control holds the raw string, and a stored value outside the curated list is prepended to the options so it stays displayable.',
      inputs: [
        { name: 'control', type: 'SchemaFormControl' },
        { name: 'label', type: 'string' },
        { name: 'placeholder', type: 'string' },
        { name: 'hideHelpText', type: 'boolean' },
      ],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'StartInstanceDialog',
      selector: 'start-instance-dialog',
      displayName: 'Start Instance Dialog',
      description:
        "Dialog for starting a new process instance with initial data. The JSON is pre-filled with the design's required properties, each set to null, so the person starting the instance sees what the start behavior will insist on and only has to fill in the values. The payload last used for this design is remembered in the browser's local storage and laid over that seed, since a process is often run again with nearly the same input.",
      inputs: [{ name: 'processDesign', type: 'ProcessDesign' }],
      outputs: [
        { name: 'started', type: 'EventEmitter<any>' },
        { name: 'startedAndRedirect', type: 'EventEmitter<any>' },
      ],
      isDialog: true,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'TenantChooserDialog',
      selector: 'tenant-chooser-dialog',
      displayName: 'Tenant Chooser Dialog',
      description:
        'Dialog that lists the current user\'s tenants and lets them select one. Administrators also get an "All Tenants" option, which clears the selection so they work across every tenant. On selection, updates the tenant service and emits the selected event (null for All Tenants).',
      inputs: [],
      outputs: [{ name: 'selected', type: 'EventEmitter<Id | null>' }],
      isDialog: true,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'TokenList',
      selector: 'token-list',
      displayName: 'Token List',
      description: 'Displays a list of tokens with activity names and status.',
      inputs: [
        { name: 'tokens', type: 'any[]' },
        { name: 'title', type: 'string' },
        { name: 'queryParams', type: 'any' },
        { name: 'activities', type: 'any[]' },
      ],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'UnclaimUserTaskButton',
      selector: 'unclaim-user-task-button',
      displayName: 'Unclaim User Task Button',
      description:
        'Button that unclaims a user task directly without a dialog.',
      inputs: [{ name: 'userTaskFormGroup', type: 'UserTaskFormGroup' }],
      outputs: [{ name: 'unclaimed', type: 'EventEmitter<void>' }],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'UpdateProcessInstanceDataButton',
      selector: 'update-process-instance-data-button',
      displayName: 'Update Process Instance Data Button',
      description:
        'Button that opens the update data dialog for a process instance.',
      inputs: [{ name: 'processInstance', type: 'ProcessInstanceFormGroup' }],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'UpdateProcessInstanceDataDialog',
      selector: 'update-process-instance-data-dialog',
      displayName: 'Update Process Instance Data Dialog',
      description: 'Dialog for updating data on a process instance.',
      inputs: [{ name: 'processInstance', type: 'ProcessInstanceFormGroup' }],
      outputs: [],
      isDialog: true,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'UpdateUserTaskDataButton',
      selector: 'update-user-task-data-button',
      displayName: 'Update User Task Data Button',
      description: 'Button that opens the update data dialog for a user task.',
      inputs: [{ name: 'userTaskFormGroup', type: 'UserTaskFormGroup' }],
      outputs: [{ name: 'dataUpdated', type: 'EventEmitter<void>' }],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'UpdateUserTaskDataDialog',
      selector: 'update-user-task-data-dialog',
      displayName: 'Update User Task Data Dialog',
      description: 'Dialog for updating data on a user task.',
      inputs: [{ name: 'userTaskFormGroup', type: 'UserTaskFormGroup' }],
      outputs: [{ name: 'dataUpdated', type: 'EventEmitter<void>' }],
      isDialog: true,
      isCustomElement: false,
      allowChildren: false,
    },
    {
      name: 'WebhookEventTypesField',
      selector: 'webhook-event-types-field',
      displayName: 'Webhook Event Types Field',
      description:
        "Multi-select picker for a subscription's event types, fed by the `listEventTypes` catalog. `*` is always offered and means every event. Writes the chosen array back to the given form control so the page's autosave picks it up.",
      inputs: [{ name: 'control', type: 'SchemaFormControl' }],
      outputs: [],
      isDialog: false,
      isCustomElement: false,
      allowChildren: false,
    },
  ];

  getMetadata(name: string): ComponentMetadata | undefined {
    return this.metadata.find((m) => m.name === name);
  }

  async loadComponent(name: string): Promise<Type<any>> {
    debug('loadComponent %s', name);
    switch (name) {
      case 'Accordion':
        return import('../../components/accordion/accordion.component').then(
          (m) => m.AccordionComponent,
        );
      case 'ActivityMappingDialog':
        return import('../../components/activity-mapping-dialog/activity-mapping-dialog.component').then(
          (m) => m.ActivityMappingDialogComponent,
        );
      case 'AddButton':
        return import('../../components/add-button/add-button.component').then(
          (m) => m.AddButtonComponent,
        );
      case 'AddDialog':
        return import('../../components/add-dialog/add-dialog.component').then(
          (m) => m.AddDialogComponent,
        );
      case 'AddField':
        return import('../../components/add-field/add-field.component').then(
          (m) => m.AddFieldComponent,
        );
      case 'Avatar':
        return import('../../components/avatar/avatar.component').then(
          (m) => m.AvatarComponent,
        );
      case 'Breadcrumb':
        return import('../../components/breadcrumb/breadcrumb.component').then(
          (m) => m.BreadcrumbComponent,
        );
      case 'BreadcrumbLevel':
        return import('../../components/breadcrumb-level/breadcrumb-level.component').then(
          (m) => m.BreadcrumbLevelComponent,
        );
      case 'ClaimServiceTaskButton':
        return import('../../components/claim-service-task-button/claim-service-task-button.component').then(
          (m) => m.ClaimServiceTaskButtonComponent,
        );
      case 'ClaimServiceTaskDialog':
        return import('../../components/claim-service-task-dialog/claim-service-task-dialog.component').then(
          (m) => m.ClaimServiceTaskDialogComponent,
        );
      case 'ClaimUserTaskButton':
        return import('../../components/claim-user-task-button/claim-user-task-button.component').then(
          (m) => m.ClaimUserTaskButtonComponent,
        );
      case 'ClaimUserTaskDialog':
        return import('../../components/claim-user-task-dialog/claim-user-task-dialog.component').then(
          (m) => m.ClaimUserTaskDialogComponent,
        );
      case 'CompleteServiceTaskButton':
        return import('../../components/complete-service-task-button/complete-service-task-button.component').then(
          (m) => m.CompleteServiceTaskButtonComponent,
        );
      case 'CompleteServiceTaskDialog':
        return import('../../components/complete-service-task-dialog/complete-service-task-dialog.component').then(
          (m) => m.CompleteServiceTaskDialogComponent,
        );
      case 'CompleteUserTaskButton':
        return import('../../components/complete-user-task-button/complete-user-task-button.component').then(
          (m) => m.CompleteUserTaskButtonComponent,
        );
      case 'CompleteUserTaskDialog':
        return import('../../components/complete-user-task-dialog/complete-user-task-dialog.component').then(
          (m) => m.CompleteUserTaskDialogComponent,
        );
      case 'ConfirmationDialog':
        return import('../../components/confirmation-dialog/confirmation-dialog.component').then(
          (m) => m.ConfirmationDialogComponent,
        );
      case 'CreateWebhookSubscriptionDialog':
        return import('../../components/create-webhook-subscription-dialog/create-webhook-subscription-dialog.component').then(
          (m) => m.CreateWebhookSubscriptionDialogComponent,
        );
      case 'DeleteButton':
        return import('../../components/delete-button/delete-button.component').then(
          (m) => m.DeleteButtonComponent,
        );
      case 'EditButton':
        return import('../../components/edit-button/edit-button.component').then(
          (m) => m.EditButtonComponent,
        );
      case 'EditDialog':
        return import('../../components/edit-dialog/edit-dialog.component').then(
          (m) => m.EditDialogComponent,
        );
      case 'ErrorDialog':
        return import('../../components/error-dialog/error-dialog.component').then(
          (m) => m.ErrorDialogComponent,
        );
      case 'ExportTsvButton':
        return import('../../components/export-tsv-button/export-tsv-button.component').then(
          (m) => m.ExportTsvButtonComponent,
        );
      case 'FireTimerButton':
        return import('../../components/fire-timer-button/fire-timer-button.component').then(
          (m) => m.FireTimerButtonComponent,
        );
      case 'FireTimerDialog':
        return import('../../components/fire-timer-dialog/fire-timer-dialog.component').then(
          (m) => m.FireTimerDialogComponent,
        );
      case 'HistoryDetailsDialog':
        return import('../../components/history-details-dialog/history-details-dialog.component').then(
          (m) => m.HistoryDetailsDialogComponent,
        );
      case 'HistoryList':
        return import('../../components/history-list/history-list.component').then(
          (m) => m.HistoryListComponent,
        );
      case 'ImportTsvButton':
        return import('../../components/import-tsv-button/import-tsv-button.component').then(
          (m) => m.ImportTsvButtonComponent,
        );
      case 'ImportTsvDialog':
        return import('../../components/import-tsv-dialog/import-tsv-dialog.component').then(
          (m) => m.ImportTsvDialogComponent,
        );
      case 'LoadFromFilesButton':
        return import('../../components/load-from-files-button/load-from-files-button.component').then(
          (m) => m.LoadFromFilesButtonComponent,
        );
      case 'LoadFromFilesDialog':
        return import('../../components/load-from-files-dialog/load-from-files-dialog.component').then(
          (m) => m.LoadFromFilesDialogComponent,
        );
      case 'ManageRoleAssignments':
        return import('../../components/manage-role-assignments/manage-role-assignments.component').then(
          (m) => m.ManageRoleAssignmentsComponent,
        );
      case 'MoveTokenDialog':
        return import('../../components/move-token-dialog/move-token-dialog.component').then(
          (m) => m.MoveTokenDialogComponent,
        );
      case 'ProcessDataPropertySection':
        return import('../../components/process-data-property-section/process-data-property-section.component').then(
          (m) => m.ProcessDataPropertySectionComponent,
        );
      case 'ProcessDataValueSection':
        return import('../../components/process-data-value-section/process-data-value-section.component').then(
          (m) => m.ProcessDataValueSectionComponent,
        );
      case 'ProcessFunctionParameterSection':
        return import('../../components/process-function-parameter-section/process-function-parameter-section.component').then(
          (m) => m.ProcessFunctionParameterSectionComponent,
        );
      case 'ProcessMessagePropertySection':
        return import('../../components/process-message-property-section/process-message-property-section.component').then(
          (m) => m.ProcessMessagePropertySectionComponent,
        );
      case 'ProcessServicePropertySection':
        return import('../../components/process-service-property-section/process-service-property-section.component').then(
          (m) => m.ProcessServicePropertySectionComponent,
        );
      case 'ProcessUserInterfacePropertySection':
        return import('../../components/process-user-interface-property-section/process-user-interface-property-section.component').then(
          (m) => m.ProcessUserInterfacePropertySectionComponent,
        );
      case 'ReassignUserTaskButton':
        return import('../../components/reassign-user-task-button/reassign-user-task-button.component').then(
          (m) => m.ReassignUserTaskButtonComponent,
        );
      case 'ReassignUserTaskDialog':
        return import('../../components/reassign-user-task-dialog/reassign-user-task-dialog.component').then(
          (m) => m.ReassignUserTaskDialogComponent,
        );
      case 'RefreshButton':
        return import('../../components/refresh-button/refresh-button.component').then(
          (m) => m.RefreshButtonComponent,
        );
      case 'RemoveRow':
        return import('../../components/remove-row/remove-row.component').then(
          (m) => m.RemoveRowComponent,
        );
      case 'ReportErrorServiceTaskButton':
        return import('../../components/report-error-service-task-button/report-error-service-task-button.component').then(
          (m) => m.ReportErrorServiceTaskButtonComponent,
        );
      case 'ReportErrorServiceTaskDialog':
        return import('../../components/report-error-service-task-dialog/report-error-service-task-dialog.component').then(
          (m) => m.ReportErrorServiceTaskDialogComponent,
        );
      case 'SearchBar':
        return import('../../components/search-bar/search-bar.component').then(
          (m) => m.SearchBarComponent,
        );
      case 'SelectCalledProcessField':
        return import('../../components/select-called-process-field/select-called-process-field.component').then(
          (m) => m.SelectCalledProcessFieldComponent,
        );
      case 'SelectProcessMessageField':
        return import('../../components/select-process-message-field/select-process-message-field.component').then(
          (m) => m.SelectProcessMessageFieldComponent,
        );
      case 'SelectProcessRoleField':
        return import('../../components/select-process-role-field/select-process-role-field.component').then(
          (m) => m.SelectProcessRoleFieldComponent,
        );
      case 'SelectProcessServiceField':
        return import('../../components/select-process-service-field/select-process-service-field.component').then(
          (m) => m.SelectProcessServiceFieldComponent,
        );
      case 'SelectProcessUserInterfaceField':
        return import('../../components/select-process-user-interface-field/select-process-user-interface-field.component').then(
          (m) => m.SelectProcessUserInterfaceFieldComponent,
        );
      case 'SelectRoleField':
        return import('../../components/select-role-field/select-role-field.component').then(
          (m) => m.SelectRoleFieldComponent,
        );
      case 'SelectRoleNameField':
        return import('../../components/select-role-name-field/select-role-name-field.component').then(
          (m) => m.SelectRoleNameFieldComponent,
        );
      case 'SelectServiceQueueField':
        return import('../../components/select-service-queue-field/select-service-queue-field.component').then(
          (m) => m.SelectServiceQueueFieldComponent,
        );
      case 'SelectUserField':
        return import('../../components/select-user-field/select-user-field.component').then(
          (m) => m.SelectUserFieldComponent,
        );
      case 'SelectUserInterfaceComponent':
        return import('../../components/select-user-interface-component/select-user-interface-component.component').then(
          (m) => m.SelectUserInterfaceComponentComponent,
        );
      case 'SendMessageButton':
        return import('../../components/send-message-button/send-message-button.component').then(
          (m) => m.SendMessageButtonComponent,
        );
      case 'SendMessageDialog':
        return import('../../components/send-message-dialog/send-message-dialog.component').then(
          (m) => m.SendMessageDialogComponent,
        );
      case 'SfCurrencyField':
        return import('../../components/sf-currency-field/sf-currency-field.component').then(
          (m) => m.SfCurrencyFieldComponent,
        );
      case 'SfDecimalField':
        return import('../../components/sf-decimal-field/sf-decimal-field.component').then(
          (m) => m.SfDecimalFieldComponent,
        );
      case 'SfIntegerField':
        return import('../../components/sf-integer-field/sf-integer-field.component').then(
          (m) => m.SfIntegerFieldComponent,
        );
      case 'SfPercentageField':
        return import('../../components/sf-percentage-field/sf-percentage-field.component').then(
          (m) => m.SfPercentageFieldComponent,
        );
      case 'SfReferenceField':
        return import('../../components/sf-reference-field/sf-reference-field.component').then(
          (m) => m.SfReferenceFieldComponent,
        );
      case 'SfTimezoneField':
        return import('../../components/sf-timezone-field/sf-timezone-field.component').then(
          (m) => m.SfTimezoneFieldComponent,
        );
      case 'StartInstanceDialog':
        return import('../../components/start-instance-dialog/start-instance-dialog.component').then(
          (m) => m.StartInstanceDialogComponent,
        );
      case 'TenantChooserDialog':
        return import('../../components/tenant-chooser-dialog/tenant-chooser-dialog.component').then(
          (m) => m.TenantChooserDialogComponent,
        );
      case 'TokenList':
        return import('../../components/token-list/token-list.component').then(
          (m) => m.TokenListComponent,
        );
      case 'UnclaimUserTaskButton':
        return import('../../components/unclaim-user-task-button/unclaim-user-task-button.component').then(
          (m) => m.UnclaimUserTaskButtonComponent,
        );
      case 'UpdateProcessInstanceDataButton':
        return import('../../components/update-process-instance-data-button/update-process-instance-data-button.component').then(
          (m) => m.UpdateProcessInstanceDataButtonComponent,
        );
      case 'UpdateProcessInstanceDataDialog':
        return import('../../components/update-process-instance-data-dialog/update-process-instance-data-dialog.component').then(
          (m) => m.UpdateProcessInstanceDataDialogComponent,
        );
      case 'UpdateUserTaskDataButton':
        return import('../../components/update-user-task-data-button/update-user-task-data-button.component').then(
          (m) => m.UpdateUserTaskDataButtonComponent,
        );
      case 'UpdateUserTaskDataDialog':
        return import('../../components/update-user-task-data-dialog/update-user-task-data-dialog.component').then(
          (m) => m.UpdateUserTaskDataDialogComponent,
        );
      case 'WebhookEventTypesField':
        return import('../../components/webhook-event-types-field/webhook-event-types-field.component').then(
          (m) => m.WebhookEventTypesFieldComponent,
        );
      default:
        throw new Error(`Unknown component: ${name}`);
    }
  }
}
